import React from 'react';
import PropTypes from 'prop-types';
import {
  Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Checkbox, IconButton, Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { formatBRL } from '../../UI';
import * as C from '../../../theme/colors.constants';
import { tiposConsultasAntecedentesTypes } from '../../../../types/tiposConsultasAntecedentesTypes';
import { rota } from '../../../prototipo.config';

/**
 * Abre o antecedente clicado numa nova aba do PROTÓTIPO (`ControleProcessosPage.jsx`
 * via `rota('/processos')`), não mais a rota real de produção (decisão
 * revertida — ajuste pedido pelo usuário sobre a Etapa 5). A nova aba lê
 * `nroProcesso` da query string no mount e reproduz o mesmo mecanismo de
 * busca por número de processo já usado em `HomePage.jsx`
 * (`buscarProcessoComFiltrosSimples` + `setResultadoAction`) — ver
 * `ControleProcessosPage.jsx`. `localStorage.setItem('previousUrl', '')`
 * (artefato do botão "Voltar" da tela real) foi removido — sem uso aqui.
 */
function abrirInspecao(nroProcesso) {
  window.open(`${rota('/processos')}?nroProcesso=${encodeURIComponent(nroProcesso)}`, '_blank');
}

/**
 * Colunas na ordem exata de `TableHeaderAntecedentesProcesso.jsx` real.
 * `campo: null` marca a única coluna não-ordenável lá (Ações — sem
 * `OrdenacaoColuna`) — mantida na mesma posição, só sem `TableSortLabel`.
 * Achado em code review: Devedor (`nomeDevedor`) é ordenável na tela real
 * (tinha sido classificada como não-ordenável por engano no port inicial).
 */
const COLUNAS = [
  { campo: 'idProcesso', label: 'Número' },
  { campo: 'acordoAtivo', label: 'Acordo Ativo', align: 'center' },
  { campo: 'nomeFase', label: 'Fase' },
  { campo: 'nomeUsuario', label: 'Negociador' },
  { campo: 'dataAbertura', label: 'Data de Abertura' },
  { campo: 'nomeCliente', label: 'Cliente' },
  { campo: 'nomeDevedor', label: 'Devedor' },
  { campo: 'valorTotal', label: 'Valor do Processo', align: 'right' },
  { campo: null, label: 'Ações', align: 'center' },
];

/**
 * Grid "Processos Antecedentes" — port de `TableHeaderAntecedentesProcesso.jsx`/
 * `TableBodyAntecedentesProcesso.jsx` reais (AW2-1033 Etapas 1-5 + ajuste
 * pós-Etapa 5). Ordenação (Etapa 2) via `TableSortLabel`; seleção (Etapa 3)
 * via checkbox por linha + select-all no cabeçalho — linha com
 * `idFase === idFaseBaixa` não é selecionável, nem individualmente nem por
 * select-all (select-all real tem uma lacuna aqui em modo `accordion`;
 * corrigida no port por decisão documentada no plano de arquitetura).
 * Checkbox (linha e select-all) também fica desabilitado enquanto
 * `tipoConsultaAntecedentes === SIMPLES` (nenhuma ação Unificar/Juntar
 * escolhida ainda) — mesmo `disabledSelection={!actionButtonType.hasType()}`
 * real; a lista em si já vem filtrada pelo tipo (fetch feito no container,
 * `ControleProcessosPage.jsx`), este componente só reflete o estado.
 * Paginação fica em `AbaAcompanhamento.jsx` (`TablePagination`, fora do
 * wrapper de scroll horizontal). Ações por linha (Etapa 5): "Inspecionar"
 * abre a rota real em nova aba; "Resumo" delega a `onAbrirResumo`
 * (fetch/estado vivem em `AbaAcompanhamento.jsx`, mesmo padrão de
 * `DialogSimularQuitacao`) — sempre disponíveis, independente do tipo.
 */
export default function TableAntecedentesProcesso({
  antecedentes, ordenacao, onChangeOrdenacao, selecionados, onSelecionar, onSelecionarTodos, idFaseBaixa,
  onAbrirResumo, tipoConsultaAntecedentes,
}) {
  // Mesmo `disabledSelection={!actionButtonType.hasType()}` real — antes de
  // escolher Unificar/Juntar (clicando no ícone correspondente em
  // `ResumoAntecedenteAtual.jsx`), nenhuma linha é selecionável. Ações de
  // linha (Inspecionar/Resumo) continuam sempre disponíveis, independente
  // do tipo — não fazem parte do `disabledSelection` real.
  const semTipoEscolhido = tipoConsultaAntecedentes === tiposConsultasAntecedentesTypes.simples;

  // Fail-closed enquanto `fasesIds` (fetch assíncrono independente, Etapa 3)
  // ainda não carregou `idFaseBaixa` — achado em code review: sem valor para
  // comparar, tratar toda linha como "não selecionável" em vez de assumir
  // "nenhuma é fase baixa" só porque o dado ainda não chegou.
  const idFaseBaixaCarregado = idFaseBaixa != null;
  const idFaseBaixaNum = Number(idFaseBaixa);
  const antecedentesSelecionaveis = idFaseBaixaCarregado
    ? antecedentes.filter(a => Number(a.idFase) !== idFaseBaixaNum)
    : [];
  const todosSelecionados = antecedentesSelecionaveis.length > 0
    && antecedentesSelecionaveis.every(a => selecionados.get(a.id));
  const algumSelecionado = antecedentesSelecionaveis.some(a => selecionados.get(a.id));

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              checked={todosSelecionados}
              indeterminate={!todosSelecionados && algumSelecionado}
              disabled={semTipoEscolhido || antecedentesSelecionaveis.length === 0}
              onChange={event => onSelecionarTodos(event.target.checked)}
            />
          </TableCell>
          {COLUNAS.map(coluna => (
            <TableCell key={coluna.label} align={coluna.align}>
              {coluna.campo ? (
                <TableSortLabel
                  active={ordenacao.campo === coluna.campo}
                  direction={ordenacao.direcao.toLowerCase()}
                  onClick={() => onChangeOrdenacao(coluna.campo)}
                >
                  {coluna.label}
                </TableSortLabel>
              ) : coluna.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {antecedentes.map((a) => {
          const desabilitado = semTipoEscolhido || !idFaseBaixaCarregado || Number(a.idFase) === idFaseBaixaNum;
          return (
            <TableRow key={a.id} hover>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={Boolean(selecionados.get(a.id)) && !desabilitado}
                  disabled={desabilitado}
                  onChange={() => onSelecionar(a.id)}
                />
              </TableCell>
              <TableCell sx={{ color: C.navy, fontWeight: 600 }}>{a.numero}</TableCell>
              <TableCell align="center">{a.acordoAtivo ? <CheckCircleIcon fontSize="small" /> : ''}</TableCell>
              <TableCell>{a.fase}</TableCell>
              <TableCell>{a.negociador}</TableCell>
              <TableCell>{a.dataAbertura}</TableCell>
              <TableCell>{a.cliente}</TableCell>
              <TableCell>{a.devedor}</TableCell>
              <TableCell align="right">{a.valorProcesso == null ? '—' : formatBRL(a.valorProcesso)}</TableCell>
              <TableCell align="center">
                <Tooltip title="Inspecionar">
                  <IconButton size="small" onClick={() => abrirInspecao(a.id)}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Resumo">
                  <IconButton size="small" onClick={() => onAbrirResumo(a.numero)}>
                    <SummarizeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

TableAntecedentesProcesso.propTypes = {
  antecedentes: PropTypes.array,
  ordenacao: PropTypes.shape({
    campo: PropTypes.string,
    direcao: PropTypes.oneOf(['ASC', 'DESC']),
  }),
  onChangeOrdenacao: PropTypes.func,
  selecionados: PropTypes.instanceOf(Map),
  onSelecionar: PropTypes.func,
  onSelecionarTodos: PropTypes.func,
  idFaseBaixa: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onAbrirResumo: PropTypes.func,
  tipoConsultaAntecedentes: PropTypes.string,
};

TableAntecedentesProcesso.defaultProps = {
  antecedentes: [],
  ordenacao: { campo: 'idProcesso', direcao: 'ASC' },
  onChangeOrdenacao: null,
  selecionados: new Map(),
  onSelecionar: null,
  onSelecionarTodos: null,
  idFaseBaixa: null,
  onAbrirResumo: null,
  tipoConsultaAntecedentes: tiposConsultasAntecedentesTypes.simples,
};
